import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../api/authAPI';
import { postSignInPath, readPendingJoinCode } from '../../organisations/pendingJoinCode';
import { continuePendingCheckoutOrChat } from '../continueAfterSignIn';
import { withAppBasename } from '../../../config/appBase';
import {
  clearPasswordChangeSignIn,
  readPasswordChangeSignIn,
} from '../passwordChangeSignIn';
import { navigateAfterPasswordLogin } from '../../../app/routes/LoginPage';
import './Login.css';

const SetPassword = () => {
  const navigate = useNavigate();
  const { logout, acceptSessionFromPasswordChange } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [alreadyChanged, setAlreadyChanged] = useState(false);

  const validate = () => {
    const next = {};
    if (!password) {
      next.password = 'Password is required';
    } else if (password.length < 8) {
      next.password = 'Password must be at least 8 characters';
    }
    if (!confirmPassword) {
      next.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      next.confirmPassword = 'Passwords do not match';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    setErrors({});
    try {
      const result = await authAPI.setPassword(password, confirmPassword);
      const newUser = acceptSessionFromPasswordChange(result);
      const record = readPasswordChangeSignIn();
      clearPasswordChangeSignIn();
      if (record?.source === 'password') {
        if (readPendingJoinCode()) {
          navigate(postSignInPath('/chat'), { replace: true });
          return;
        }
        await navigateAfterPasswordLogin(navigate, record.locationState, newUser);
        return;
      }
      if (record?.source === 'google' || record?.source === 'linkedin' || record?.source === 'magic') {
        await continuePendingCheckoutOrChat({ navigate, user: newUser });
        return;
      }
      const isCertAdmin = newUser?.role === 'certadmin';
      navigate(postSignInPath(isCertAdmin ? '/cert-portal' : '/chat'), { replace: true });
    } catch (err) {
      if (err?.status === 403 && err?.data?.error === 'password_change_not_required') {
        setAlreadyChanged(true);
        return;
      }
      setErrors({ submit: err?.data?.error || err?.message || 'Could not save the password.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="workay-auth-wrapper">
      <section className="workay-hero-section">
        <div className="workay-form-pane">
          <div className="workay-form-content">
            <div className="workay-brand-header">
              <a href={withAppBasename('/')} className="workay-logo-link">
                <img src="/logo-yellow.png" alt="Pathbuddy Logo" className="workay-logo-img" />
                <span className="workay-brand-title">Pathbuddy</span>
              </a>
            </div>
            <div className="workay-headline-group">
              <h1 className="workay-title">Set a new password</h1>
              <p className="workay-subtitle">
                Choose a password for your account. You will use it the next time you sign in.
              </p>
            </div>
            {alreadyChanged ? (
              <p>Your password was already changed. Log out and sign in with the new password.</p>
            ) : (
              <form className="workay-form" onSubmit={handleSubmit} noValidate>
                {errors.submit && <div className="workay-alert error">{errors.submit}</div>}
                <div className="workay-field">
                  <label className="workay-label" htmlFor="new-password">New password</label>
                  <input
                    id="new-password"
                    className="workay-input"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                  {errors.password && <span className="workay-field-error">{errors.password}</span>}
                </div>
                <div className="workay-field">
                  <label className="workay-label" htmlFor="confirm-new-password">Confirm new password</label>
                  <input
                    id="confirm-new-password"
                    className="workay-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    autoComplete="new-password"
                  />
                  {errors.confirmPassword && (
                    <span className="workay-field-error">{errors.confirmPassword}</span>
                  )}
                </div>
                <button type="submit" className="workay-submit-btn" disabled={isLoading}>
                  Save password
                </button>
              </form>
            )}
            <div className="workay-footer-toggle">
              <button type="button" className="workay-link-underline" onClick={handleLogout}>
                Log out
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SetPassword;
